import React, { useState, useEffect, useMemo } from "react";
import "../SearchContainer.css";

import "./RequestedLeaves.css";
import "animate.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import dayjs from "dayjs";
import { countWorkingDaysBetween, buildHolidaySet, toPktDateStr } from "../../../utils/leaveDays";

import {
  Button,
  TableContainer,
  IconButton,
  Snackbar,
  Alert,
  ButtonGroup,
  Pagination,
  Select,
  MenuItem,
  Popover,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTheme } from "../../../context/ThemeContext";

const MAX_TRACKS = 3;

const Leaves = (props) => {
  const user = props.user;
  const { theme } = useTheme();

  const [showLeaves, setShowLeaves] = useState(true);
  const [leaveRows, setLeaveRows] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [holidaySet, setHolidaySet] = useState(() => new Set());

  // Load company holidays so the day count shown to HR excludes weekends AND
  // holidays, matching the balance that actually gets deducted.
  useEffect(() => {
    const loadHolidays = async () => {
      try {
        const res = await fetch("https://hr-backend-72v1.onrender.com/holidays");
        if (res.ok) {
          const data = await res.json();
          setHolidaySet(buildHolidaySet(Array.isArray(data) ? data : []));
        }
      } catch (e) {
        /* best-effort: weekends still excluded even without holiday data */
      }
    };
    loadHolidays();
  }, []);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [notifStatus, setNotifStatus] = useState("pending");

  const handleToggle = (tab) => {
    setShowLeaves(tab === "leaves");
  };

  const fetchPendingNotifications = async () => {
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/pendingnotifications?page=${currentPage}`
      );
      const { data, total } = await response.json();
      setNotifications(data);
      setTotalPages(Math.ceil(total / 10));
    } catch (error) {
      console.error("Error fetching pending notifications:");
    }
  };

  const fetchApprovedNotifications = async () => {
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/approvednotifications?page=${currentPage}`
      );
      const { data, total } = await response.json();
      setNotifications(data);
      setTotalPages(Math.ceil(total / 10));
    } catch (error) {
      console.error("Error fetching approved notifications:");
    }
  };

  useEffect(() => {
    if (notifStatus === "pending") {
      fetchPendingNotifications();
    } else if (notifStatus === "approved") {
      fetchApprovedNotifications();
    }
  }, [currentPage, notifStatus]);

  const handleStatusChange = (newStatus) => {
    setNotifStatus(newStatus);
    setCurrentPage(1);
  };

  const approveRequest = async (request) => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/approveRequest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: request._id }),
      });
      console.log("Function running ", request._id);
      const data = await response.json();
      if (data.success) {
        setAlertMessage("Request accepted");
        setShowAlert(true);
        if (notifStatus === "pending") fetchPendingNotifications();
        else fetchApprovedNotifications();
      } else {
        alert("Error approving request");
      }
    } catch (error) {
      console.error("Error approving request:");
    }
  };

  const rejectRequest = async (id) => {
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/rejectRequest/${id}`,
        { method: "DELETE" }
      );
      const data = await response.json();
      if (data.success) {
        setAlertMessage("Request rejected");
        setShowAlert(true);
        if (notifStatus === "pending") fetchPendingNotifications();
        else fetchApprovedNotifications();
      } else {
        alert("Error rejecting request");
      }
    } catch (error) {
      console.error("Error rejecting request:");
    }
  };

  const handleCloseAlert = () => setShowAlert(false);

  const fetchLeaves = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/showleaves");
      const data = await response.json();

      if (response.ok) {
        const formattedData = data.map((leave) => {
          return {
            ...leave,
            dateRange: `${toPktDateStr(leave.fromDate)} | ${toPktDateStr(leave.toDate)}`,
          };
        });

        if (user.roleM === "Manager" || user.roleM === "HR") {
          const matchedUsers = formattedData.filter((fd) =>
            user.leads.some((lead) => lead.email === fd.email)
          );
          setLeaveRows(matchedUsers.reverse());
        } else {
          setLeaveRows(formattedData.reverse());
        }
      } else {
        console.error("Failed to fetch leaves:");
      }
    } catch (error) {
      console.error("Error fetching leaves:");
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const leaveAcceptHandler = (role, email, leaveId, fromDate, toDate, leaveType) => {
    handleLeaveUpdate(role, email, "Accepted", leaveId, fromDate, toDate, leaveType);
  };

  const leaveRejectHandler = (role, email, leaveId, fromDate, toDate, leaveType) => {
    handleLeaveUpdate(role, email, "Rejected", leaveId, fromDate, toDate, leaveType);
  };

  const handleLeaveUpdate = async (role, email, status, leaveId, fromDate, toDate, leaveType) => {
    try {
      const response = await fetch(`https://hr-backend-72v1.onrender.com/update-leave-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role,
          email,
          status: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
          leaveId,
          fromDate,
          toDate,
          leaveType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update leave status");
      }

      await response.json();
      setPopoverAnchor(null);
      setSelectedLeave(null);
      fetchLeaves();
      alert("Leave State Updated");
    } catch (error) {
      console.error("Error updating leave status:");
      alert(error.message);
    }
  };

  // ─── Calendar helpers ────────────────────────────────────────────

  const getFirstName = (name) => {
    if (!name) return "";
    return name.split(" ")[0];
  };

  /**
   * Returns one of: "accepted" | "hr-pending" | "manager-pending" | "rejected"
   */
  const getLeaveStatus = (leave) => {
    const ms = leave.managerStatus || "Pending";
    const hs = leave.hrStatus || "Pending";
    if (ms === "Rejected" || hs === "Rejected") return "rejected";
    if (ms === "Accepted" && hs === "Accepted") return "accepted";
    if (ms === "Accepted" && hs === "Pending") return "hr-pending";
    return "manager-pending"; // managerStatus Pending
  };

  const getStatusLabel = (leave) => {
    switch (getLeaveStatus(leave)) {
      case "accepted":        return "✓ Approved";
      case "hr-pending":      return "(Awaiting HR Approval)";
      case "manager-pending": return "(Awaiting Manager Approval)";
      case "rejected":        return "✗ Rejected";
      default:                return "";
    }
  };

  const getBarLabel = (leave) =>
    `${getFirstName(leave.name)} · ${leave.leaveType} ${getStatusLabel(leave)}`;

  const buildCalendarDays = (month) => {
    const startOfMonth = month.startOf("month");
    const startDay = startOfMonth.day();
    const startDate = startOfMonth.subtract(startDay, "day");
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = startDate.add(i, "day");
      days.push({ date, isCurrentMonth: date.month() === month.month() });
    }
    return days;
  };

  const buildWeekRows = (month) => {
    const days = buildCalendarDays(month);
    const weeks = [];
    for (let w = 0; w < 6; w++) {
      weeks.push(days.slice(w * 7, w * 7 + 7));
    }
    return weeks;
  };

  // ALL leaves (no status filter)
  const leavesMap = useMemo(() => {
    const map = new Map();
    leaveRows.forEach((leave) => {
      const from = dayjs(leave.fromDate);
      const to = dayjs(leave.toDate);
      let current = from;
      while (current.isBefore(to) || current.isSame(to, "day")) {
        const key = current.format("YYYY-MM-DD");
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(leave);
        current = current.add(1, "day");
      }
    });
    return map;
  }, [leaveRows]);

  // For a week row, collect unique leaves and assign non-overlapping tracks
  const getWeekBars = (weekDays) => {
    const weekStart = weekDays[0].date;
    const weekEnd = weekDays[6].date;

    const seenIds = new Set();
    const weekLeaves = [];
    weekDays.forEach(({ date }) => {
      const key = date.format("YYYY-MM-DD");
      (leavesMap.get(key) || []).forEach((leave) => {
        if (!seenIds.has(leave._id)) {
          seenIds.add(leave._id);
          weekLeaves.push(leave);
        }
      });
    });

    // Sort by effective start in this week, then by status priority
    const statusOrder = { "manager-pending": 0, "hr-pending": 1, accepted: 2, rejected: 3 };
    weekLeaves.sort((a, b) => {
      const aStart = dayjs(a.fromDate).isBefore(weekStart) ? weekStart : dayjs(a.fromDate);
      const bStart = dayjs(b.fromDate).isBefore(weekStart) ? weekStart : dayjs(b.fromDate);
      const dateDiff = aStart.diff(bStart);
      if (dateDiff !== 0) return dateDiff;
      return (statusOrder[getLeaveStatus(a)] ?? 4) - (statusOrder[getLeaveStatus(b)] ?? 4);
    });

    const trackEnds = [];
    const bars = weekLeaves.map((leave) => {
      const from = dayjs(leave.fromDate);
      const to = dayjs(leave.toDate);
      const barStart = from.isBefore(weekStart) ? weekStart : from;
      const barEnd = to.isAfter(weekEnd) ? weekEnd : to;
      const startCol = barStart.day() + 1;
      const endCol = barEnd.day() + 2; // exclusive

      let track = trackEnds.findIndex((endC) => endC <= startCol);
      if (track === -1) track = trackEnds.length;
      trackEnds[track] = endCol;

      const showLabel = !from.isBefore(weekStart);

      return { leave, startCol, endCol, track, showLabel };
    });

    return {
      visible: bars.filter((b) => b.track < MAX_TRACKS),
      overflow: bars.filter((b) => b.track >= MAX_TRACKS),
    };
  };

  const weekRows = buildWeekRows(currentMonth);
  const today = dayjs().format("YYYY-MM-DD");
  const dayHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const handleBarClick = (e, leave) => {
    e.stopPropagation();
    setSelectedLeave(leave);
    setPopoverAnchor(e.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSelectedLeave(null);
  };

  const selectedStatus = selectedLeave ? getLeaveStatus(selectedLeave) : null;

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div>
      {(user.role === "HR" || user.role === "Executive") && (
        <div
          style={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
            gap: "20px",
            zIndex: "1000",
          }}
        >
          <Button
            variant={showLeaves ? "contained" : "outlined"}
            onClick={() => handleToggle("leaves")}
          >
            Leaves
          </Button>
          <Button
            variant={!showLeaves ? "contained" : "outlined"}
            onClick={() => handleToggle("notifications")}
          >
            Notifications
          </Button>
        </div>
      )}

      {showLeaves ? (
        <div className="leave-calendar">
          {/* Calendar Header */}
          <div className="leave-calendar__header">
            <div className="leave-calendar__nav">
              <IconButton
                className="leave-calendar__nav-btn"
                onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
              >
                <ChevronLeftIcon />
              </IconButton>
              <h3 className="leave-calendar__title">
                {currentMonth.format("MMMM YYYY")}
              </h3>
              <IconButton
                className="leave-calendar__nav-btn"
                onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
              >
                <ChevronRightIcon />
              </IconButton>
            </div>

            {/* 4-item legend */}
            <div className="leave-calendar__legend">
              <span className="leave-calendar__legend-item">
                <span className="leave-calendar__legend-dot leave-calendar__legend-dot--accepted" />
                Approved
              </span>
              <span className="leave-calendar__legend-item">
                <span className="leave-calendar__legend-dot leave-calendar__legend-dot--hr-pending" />
                Awaiting HR
              </span>
              <span className="leave-calendar__legend-item">
                <span className="leave-calendar__legend-dot leave-calendar__legend-dot--manager-pending" />
                Awaiting Manager
              </span>
              <span className="leave-calendar__legend-item">
                <span className="leave-calendar__legend-dot leave-calendar__legend-dot--rejected" />
                Rejected
              </span>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="leave-calendar__grid">
            {dayHeaders.map((day) => (
              <div key={day} className="leave-calendar__day-header">
                {day}
              </div>
            ))}

            {weekRows.map((weekDays, weekIdx) => {
              const { visible: weekBars, overflow: overflowBars } = getWeekBars(weekDays);
              return (
                <React.Fragment key={weekIdx}>
                  {weekDays.map(({ date, isCurrentMonth }, dayIdx) => {
                    const dateKey = date.format("YYYY-MM-DD");
                    const isToday = dateKey === today;
                    return (
                      <div
                        key={dayIdx}
                        className={[
                          "leave-calendar__cell",
                          !isCurrentMonth ? "leave-calendar__cell--outside" : "",
                          isToday ? "leave-calendar__cell--today" : "",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "leave-calendar__day-number",
                            isToday ? "leave-calendar__day-number--today" : "",
                          ].join(" ")}
                        >
                          {date.date()}
                        </span>
                      </div>
                    );
                  })}

                  {/* Spanning bar row */}
                  <div className="leave-calendar__bar-row">
                    {weekBars.map(({ leave, startCol, endCol, track, showLabel }) => (
                      <button
                        key={`${leave._id}-w${weekIdx}`}
                        className={`leave-calendar__bar leave-calendar__bar--${getLeaveStatus(leave)}`}
                        style={{
                          gridColumn: `${startCol} / ${endCol}`,
                          gridRow: track + 1,
                        }}
                        onClick={(e) => handleBarClick(e, leave)}
                        title={getBarLabel(leave)}
                      >
                        {showLabel ? getBarLabel(leave) : "\u00A0"}
                      </button>
                    ))}
                    {overflowBars.length > 0 && (
                      <div
                        className="leave-calendar__bar-overflow"
                        style={{ gridColumn: "1 / -1", gridRow: MAX_TRACKS + 1 }}
                      >
                        +{overflowBars.length} more
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Leave Detail Popover */}
          <Popover
            open={Boolean(popoverAnchor) && Boolean(selectedLeave)}
            anchorEl={popoverAnchor}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{ paper: { className: "leave-calendar__popover" } }}
          >
            {selectedLeave && (
              <div className="leave-calendar__popover-content">
                <div className="leave-calendar__popover-header">
                  <h4 className="leave-calendar__popover-name">
                    {selectedLeave.name}
                  </h4>
                  <IconButton
                    size="small"
                    onClick={handleClosePopover}
                    className="leave-calendar__popover-close"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>

                <span className="leave-calendar__popover-type">
                  {selectedLeave.leaveType}
                </span>

                <div className="leave-calendar__popover-dates">
                  {dayjs(toPktDateStr(selectedLeave.fromDate)).format("DD MMM")} -{" "}
                  {dayjs(toPktDateStr(selectedLeave.toDate)).format("DD MMM")}
                  <span className="leave-calendar__popover-days">
                    &nbsp;(
                    {countWorkingDaysBetween(
                      selectedLeave.fromDate,
                      selectedLeave.toDate,
                      holidaySet
                    )}{" "}
                    day
                    {countWorkingDaysBetween(
                      selectedLeave.fromDate,
                      selectedLeave.toDate,
                      holidaySet
                    ) !== 1
                      ? "s"
                      : ""}
                    )
                  </span>
                </div>

                <div className="leave-calendar__popover-statuses">
                  <span
                    className={`leave-calendar__status-badge leave-calendar__status-badge--${
                      selectedLeave.managerStatus === "Accepted"
                        ? "accepted"
                        : selectedLeave.managerStatus === "Rejected"
                        ? "rejected"
                        : "pending"
                    }`}
                  >
                    Manager: {selectedLeave.managerStatus || "Pending"}
                  </span>
                  <span
                    className={`leave-calendar__status-badge leave-calendar__status-badge--${
                      selectedLeave.hrStatus === "Accepted"
                        ? "accepted"
                        : selectedLeave.hrStatus === "Rejected"
                        ? "rejected"
                        : "pending"
                    }`}
                  >
                    HR: {selectedLeave.hrStatus || "Pending"}
                  </span>
                </div>

                {selectedLeave.reason && (
                  <div className="leave-calendar__popover-reason">
                    <strong>Reason:</strong> {selectedLeave.reason}
                  </div>
                )}

                {/* HR action buttons: HR-pending leaves, plus manager-pending leaves
                    whose manager IS HR — HR settles both steps in one decision. */}
                {(selectedStatus === "hr-pending" ||
                  (selectedStatus === "manager-pending" && selectedLeave?.managedByHr)) &&
                  (user.role === "HR" || user.role === "Executive") && (
                    <div className="leave-calendar__popover-actions">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<DoneIcon />}
                        onClick={() =>
                          leaveAcceptHandler(
                            "hr",
                            selectedLeave.email,
                            selectedLeave._id,
                            selectedLeave.fromDate,
                            selectedLeave.toDate,
                            selectedLeave.leaveType
                          )
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() =>
                          leaveRejectHandler(
                            "hr",
                            selectedLeave.email,
                            selectedLeave._id,
                            selectedLeave.fromDate,
                            selectedLeave.toDate,
                            selectedLeave.leaveType
                          )
                        }
                      >
                        Decline
                      </Button>
                    </div>
                  )}

                {/* Manager action - manager-pending leaves */}
                {selectedStatus === "manager-pending" &&
                  user.roleM === "Manager" && (
                    <div className="leave-calendar__popover-actions">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<DoneIcon />}
                        onClick={() =>
                          leaveAcceptHandler(
                            "manager",
                            selectedLeave.email,
                            selectedLeave._id,
                            selectedLeave.fromDate,
                            selectedLeave.toDate,
                            selectedLeave.leaveType
                          )
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() =>
                          leaveRejectHandler(
                            "manager",
                            selectedLeave.email,
                            selectedLeave._id,
                            selectedLeave.fromDate,
                            selectedLeave.toDate,
                            selectedLeave.leaveType
                          )
                        }
                      >
                        Decline
                      </Button>
                    </div>
                  )}
              </div>
            )}
          </Popover>
        </div>
      ) : (
        <TableContainer component={Paper} className="notificationContainer">
          <h4 className="myTableHeader">NOTIFICATIONS</h4>
          <ButtonGroup
            color="primary"
            aria-label="outlined primary"
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "50px",
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            <Button
              variant={notifStatus === "pending" ? "contained" : "outlined"}
              onClick={() => handleStatusChange("pending")}
            >
              Pending
            </Button>
            <Button
              variant={notifStatus === "approved" ? "contained" : "outlined"}
              onClick={() => handleStatusChange("approved")}
            >
              Approved
            </Button>
          </ButtonGroup>

          <Table sx={{ minWidth: 650 }} className="notificationTable">
            <TableHead>
              <TableRow>
                <TableCell className="myTableHeadCells">
                  <span className="myTableHeadSpan">Name</span>
                </TableCell>
                <TableCell className="myTableHeadCells">
                  <span className="myTableHeadSpan">Field</span>
                </TableCell>
                <TableCell className="myTableHeadCells">
                  <span className="myTableHeadSpan">Requested Change</span>
                </TableCell>
                <TableCell className="myTableHeadCells">
                  <span className="myTableHeadSpan">Status</span>
                </TableCell>
                {(user.role === "HR" || user.role === "Executive") && (
                  <TableCell className="myTableHeadCells">
                    <span className="myTableHeadSpan">Actions</span>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map((request) => (
                <TableRow key={request._id}>
                  <TableCell sx={{ fontSize: "larger" }}>{request.name}</TableCell>
                  <TableCell sx={{ fontSize: "larger" }}>{request.field}</TableCell>
                  <TableCell>
                    {request.value.includes("https") ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => window.open(request.value, "_blank")}
                      >
                        Open Link
                      </Button>
                    ) : (
                      request.value
                    )}
                  </TableCell>
                  {request.status === "pending" ? (
                    <TableCell sx={{ fontSize: "medium" }}>
                      <span className="pending">{request.status}</span>
                    </TableCell>
                  ) : (
                    <TableCell sx={{ fontSize: "medium" }}>
                      <span className="accepted">{request.status}</span>
                    </TableCell>
                  )}
                  {(user.role === "HR" ||
                    user.roleM === "Manager" ||
                    user.role === "Executive") && (
                    <TableCell sx={{ fontSize: "larger" }}>
                      <div style={{ display: "flex", flexDirection: "row", gap: "18px" }}>
                        {request.status === "pending" ? (
                          <>
                            <IconButton color="success" onClick={() => approveRequest(request)}>
                              <DoneIcon />
                            </IconButton>
                            <IconButton color="error" onClick={() => rejectRequest(request._id)}>
                              <CloseIcon />
                            </IconButton>
                          </>
                        ) : (
                          "Already Taken"
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "20px",
              margin: "20px 0",
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
            />
            <Select
              value={currentPage}
              onChange={(e) => setCurrentPage(Number(e.target.value))}
              sx={{ minWidth: 120 }}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              {Array.from({ length: totalPages }, (_, index) => (
                <MenuItem key={index + 1} value={index + 1}>
                  Page {index + 1}
                </MenuItem>
              ))}
            </Select>
          </div>
        </TableContainer>
      )}

      <Snackbar
        open={showAlert}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertMessage.includes("accepted") ? "success" : "error"}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Leaves;
